<i18n locale="en">
{
  "confirm-send": "Are you sure you want to send this campaign now?",
  "titles": {
    "schedule": "Change schedule",
    "subject": "Update subject",
    "audience": "Update subscriber audience",
    "send": "Send",
    "send-email": "Send email"
  },
  "errors": {
    "update": "There was a problem updating the campaign",
    "schedule": "There was a problem scheduling this campaign",
    "schedule-draft": "There was a problem scheduling this campaign. It has been saved as a draft."
  }
}
</i18n>

<template>
  <OwnDialog
    v-if="campaign"
    v-model="showDialog"
    data-testid="schedule-campaign-dialog"
  >
    <template #heading="{ close }">
      <InvalidConfigurationHeader v-if="showWarning" @cancel="close" />
      <OwnDialogHeading v-else :title="dialogTitle" @cancel="close" />
    </template>

    <OwnDialogContent>
      <InvalidConfigurationContent
        v-if="showWarning"
        :warnings="warnings"
        :unedited="uneditedSections"
        :has-app-unavailable="hasAppUnavailableSection"
      />
      <div v-else class="flex-col gap-8">
        <OwnType
          v-if="action === 'send' && campaign.transport !== 'email'"
          :text="t('confirm-send')"
        />
        <OTCEditor
          v-else
          v-model:date="executeAt"
          v-model:subject="subject"
          v-model:location-id="locationId"
          v-model:disabled="isDisabledByEditor"
          :edit-schedule="editSchedule"
          :edit-subject="editSubject"
          :edit-audience="editAudience"
        />
        <OwnRule v-if="hasCustomContent" type="dashed" />
        <slot
          name="custom-content"
          :disable="onToggleDisabledByCustomContent"
          :execute-at="executeAt"
        />
      </div>
    </OwnDialogContent>

    <template #footer>
      <OwnDialogFooter>
        <InvalidConfigurationFooter
          v-if="showWarning"
          :campaign="campaign"
          @close="showDialog = false"
        />
        <UpdateOTCFooter
          v-else-if="actionType === 'update'"
          :disabled="isDisabled"
          :saving="isSaving"
          @update="onUpdate"
        />
        <ScheduleOTCFooter
          v-else
          :date="executeAt"
          :disabled="isDisabled"
          :subject="subject"
          :campaign="campaign"
          :saving="isSaving"
          @schedule="onSchedule"
        />
      </OwnDialogFooter>
    </template>
  </OwnDialog>
</template>

<script lang="ts" setup>
import { isDefined } from '@vueuse/core'
import { computed, onMounted, ref, useSlots, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { type BuilderMethods } from '@/lib/builder/types'
import { logError } from '@/logger'
import { OlympusClient } from '@/OlympusClient'
import { useMappedAction, useMappedGetter } from '@/store'
import {
  OwnDialog,
  OwnDialogContent,
  OwnDialogFooter,
  OwnDialogHeading,
} from '@/ui/OwnDialog'
import { OwnRule } from '@/ui/OwnRule'
import { OwnType } from '@/ui/OwnType'
import { useNotify } from '@/utils/useNotify'

import { getBrandHasApp } from '../../../builders/email/composables/useBrandHasApp'
import { isUneditedTemplateText } from '../../../builders/email/templates/defaultCopy'
import { useCustomCampaignsMe2EditsFlag } from '../../../composables/useCustomCampaignsMe2EditsFlag'
import { useCustomCampaignsMe2Flag } from '../../../composables/useCustomCampaignsMe2Flag'
import { sendMe2ConvergentEdit } from '../../../sendMe2ConvergentEdit'
import {
  BaseCampaignResource,
  CampaignBlock,
  MarketingStoreModule,
} from '../../../types'
import OTCEditor from '../OTCEditor.vue'
import ScheduleOTCFooter from '../ScheduleOTCFooter.vue'

import InvalidConfigurationContent from './InvalidConfigurationContent.vue'
import InvalidConfigurationFooter from './InvalidConfigurationFooter.vue'
import InvalidConfigurationHeader from './InvalidConfigurationHeader.vue'
import UpdateOTCFooter from './UpdateOTCFooter.vue'

const { t } = useI18n()
const notify = useNotify()
const route = useRoute()
const router = useRouter()
const useScheduleEndpoint = useCustomCampaignsMe2Flag()
const me2ConvergentEditEnabled = useCustomCampaignsMe2EditsFlag()

const props = withDefaults(
  defineProps<{
    /**
     * Specific sub-operations to perform with the dialog
     */
    action?: 'schedule' | 'subject' | 'audience' | 'send'

    /**
     * When present, will be used for the call to `publishChanges`.
     */
    blockModules?: Array<unknown>

    methods: BuilderMethods<
      BaseCampaignResource,
      unknown,
      unknown,
      CampaignBlock
    >

    storeModule: MarketingStoreModule
  }>(),
  {
    action: undefined,
    blockModules: () => [],
    methods: undefined,
  }
)
const showDialog = defineModel<boolean>({ default: false })
const emit = defineEmits<{ submit: [] }>()

const slots = useSlots()
// `$slots['custom-content']` in the template doesn't work because it's not reactive, so we use a computed prop instead
const hasCustomContent = computed(() => !!slots['custom-content'])

const executeAt = ref(new Date())
const isDisabledByEditor = ref(false)
const isDisabledByCustomContent = ref(false)
const isDisabled = computed(
  () => isDisabledByEditor.value || isDisabledByCustomContent.value
)
const isSaving = ref(false)
const locationId = ref('')
const subject = ref('')

const campaign = useMappedGetter<BaseCampaignResource | undefined>(
  `${props.storeModule}/currentResource`
)
const getBlockById = useMappedGetter<
  (blockId: string) => CampaignBlock | undefined
>(`${props.storeModule}/blockById`)

const actionType = computed(() => {
  return props.action === 'subject' || props.action === 'audience'
    ? 'update'
    : 'schedule'
})

const dialogTitle = computed(() => {
  const key = props.action ?? 'send'
  if (key === 'send' && campaign.value?.transport === 'email') {
    return t('titles.send-email')
  }
  return t(`titles.${key}`)
})

const editSchedule = computed(() => {
  return !props.action || props.action === 'schedule'
})

const editSubject = computed(() => {
  return (
    campaign.value?.transport === 'email' &&
    (!props.action || props.action === 'send' || props.action === 'subject')
  )
})

const editAudience = computed(() => {
  return props.action === 'audience'
})

const hasAppUnavailableSection = computed(() => {
  if (!campaign.value) return false

  return campaign.value.sections.some((sectionId) => {
    const section = getBlockById.value(sectionId)
    return (
      section?.type === 'get-the-app-buttons' &&
      getBrandHasApp(campaign.value?.brandId) === false
    )
  })
})

const showWarning = computed(() => {
  // We can always allow subject and audience editing
  if (props.action === 'subject' || props.action === 'audience') return false

  return (
    !!warnings.value.length ||
    hasAppUnavailableSection.value ||
    !!uneditedSections.value.length
  )
})

// Section types that do not require configuration data (null is expected/valid)
const SECTION_TYPES_WITH_NULL_DATA = [
  'get-the-app-buttons',
  'gift-card',
] as const

const warnings = computed<CampaignBlock['type'][]>(() => {
  if (!campaign.value) return []

  return campaign.value.sections
    .map((sectionId) => {
      const section = getBlockById.value(sectionId)

      if (
        section &&
        section.data === null &&
        !SECTION_TYPES_WITH_NULL_DATA.includes(
          section.type as (typeof SECTION_TYPES_WITH_NULL_DATA)[number]
        )
      )
        return section.type

      return null
    })
    .filter((value) => isDefined(value))
})

// Text sections seeded by a starter template carry placeholder copy. If the
// user never replaced it, block the send so stock copy can't ship to guests.
// Only heading/paragraph have seeded defaults (see email-builder-en.ts).
const TEXT_SECTION_TYPES = ['heading', 'paragraph'] as const

const uneditedSections = computed<CampaignBlock['type'][]>(() => {
  if (!campaign.value) return []

  // De-dup by type: the warning list shows typed labels ("Text (heading)"),
  // so multiple unedited sections of the same type collapse to one entry.
  return Array.from(
    new Set(
      campaign.value.sections
        .map((sectionId) => {
          const section = getBlockById.value(sectionId)

          if (
            section &&
            TEXT_SECTION_TYPES.includes(
              section.type as (typeof TEXT_SECTION_TYPES)[number]
            ) &&
            isUneditedTemplateText(
              (section.data as { text?: string } | null)?.text
            )
          )
            return section.type

          return null
        })
        .filter((value) => isDefined(value))
    )
  )
})

const initialize = function () {
  if (!campaign.value) return

  subject.value = campaign.value.name
  locationId.value = campaign.value.locationId || ''

  if (props.action === 'send' || !campaign.value.executeAt) {
    executeAt.value = new Date()
  } else {
    executeAt.value = new Date(campaign.value.executeAt)
  }
}

const onSchedule = async function () {
  await submitChanges('schedule')
}

const onUpdate = async function () {
  await submitChanges('update')
}

const onToggleDisabledByCustomContent = (newValue: boolean) => {
  isDisabledByCustomContent.value = newValue
}

const publishChanges = useMappedAction(`${props.storeModule}/publishChanges`)
const updateCampaign = useMappedAction(`${props.storeModule}/updateResource`)
const resetResource = useMappedAction(
  `${props.storeModule}/_builder_resetResource`
)
const scheduleCampaign =
  OlympusClient.marketing.v1.campaigns.campaign.scheduleCustomCampaign

// Roll a just-published campaign back to draft (clear executeAt) so a failure
// after publish never leaves it published-but-unscheduled. Returns to the
// overview and notifies. Shared by the publish-failure and schedule-failure
// paths so both restore the same atomicity the pre-split flow had.
const rollbackToDraft = async function (campaignId: string): Promise<void> {
  try {
    await OlympusClient.marketing.v1.campaigns.campaign.updateCampaign({
      body: { executeAt: null, status: 'draft' },
      params: { campaignId },
    })
    notify(t('errors.schedule-draft'), 'error')
  } catch {
    notify(t('errors.schedule'), 'error')
  }
  await router.push({
    name: 'brand.marketing.otc.overview',
    params: route.params,
  })
}

// Legacy submit path for both update and schedule modes: persist content (and,
// for schedule, `executeAt`) in a single PATCH and never call the `/schedule`
// endpoint, exactly matching pre-ME2 `main`. Taken whenever `useScheduleEndpoint`
// is off, so flag-off is a faithful full rollback and the Hermes deploy can ship
// ahead of the Olympus `/schedule` endpoint.
const submitLegacyChanges = async function (mode: 'schedule' | 'update') {
  try {
    isSaving.value = true

    if (props.action === 'send') executeAt.value = new Date()

    const update: Record<string, unknown> = {
      name: subject.value,
      subject: subject.value,
    }
    if (props.action === 'audience') update.locationId = locationId.value
    if (mode !== 'update') update.executeAt = executeAt.value

    await updateCampaign(update)

    const result = (await publishChanges({
      blocks: props.blockModules,
      methods: props.methods,
    })) as { state: 'error' | 'success'; errors: string[] } | undefined

    if (result?.state === 'error') {
      if (mode !== 'update' && campaign.value) {
        try {
          await OlympusClient.marketing.v1.campaigns.campaign.updateCampaign({
            body: { executeAt: null, status: 'draft' },
            params: { campaignId: campaign.value.id },
          })
          notify(t('errors.schedule-draft'), 'error')
        } catch {
          notify(t('errors.schedule'), 'error')
        }
        await router.push({
          name: 'brand.marketing.otc.overview',
          params: route.params,
        })
        return
      }
      throw new Error('publish-failed')
    }

    if (mode === 'update') {
      showDialog.value = false
    } else {
      emit('submit')
    }
  } catch (error) {
    logError(error)
    notify(t(`errors.${mode}`), 'error')
  } finally {
    isSaving.value = false
  }
}

const submitChanges = async function (mode: 'schedule' | 'update') {
  if (!useScheduleEndpoint.value) {
    await submitLegacyChanges(mode)
    return
  }

  try {
    isSaving.value = true

    // Capture the intended send time BEFORE the awaits below. `updateCampaign`
    // and `publishChanges` replace the builder's `currentResource`, which
    // re-fires `watch(campaign, initialize)`; for a fresh draft (stored
    // executeAt null) that re-init resets `executeAt.value` to now, clobbering
    // the user's picked time. A genuine "Send now" still wants now.
    const scheduledFor = props.action === 'send' ? new Date() : executeAt.value
    // Captured synchronously: the awaits below replace the builder's
    // `currentResource`, so `campaign.value` can point at a different object by
    // the time the post-schedule refresh / ME2 convergent push runs.
    const brandId = campaign.value?.brandId
    const editedCampaignId = campaign.value?.id

    // The update path persists name/subject (+ audience) via PATCH and never
    // carries executeAt. The schedule path persists the same content here, then
    // commits executeAt through the dedicated `/schedule` endpoint below — which
    // also hands ME2-routed brands off to ME2.
    const update: Record<string, unknown> = {
      name: subject.value,
      subject: subject.value,
    }
    // On an audience edit always persist the selection — the chosen location or
    // null for whole-brand, never omit. The convergent edit below re-resolves
    // locationId from the stored campaign row, so an un-persisted change never
    // reaches ME2; omitting an empty value silently dropped a clear back to
    // whole-brand. null (not '') satisfies the validator's nullable locationId.
    if (props.action === 'audience') {
      update.locationId = locationId.value || null
    }

    await updateCampaign(update)

    const result = (await publishChanges({
      blocks: props.blockModules,
      methods: props.methods,
    })) as { state: 'error' | 'success'; errors: string[] } | undefined

    if (result?.state === 'error') {
      if (mode !== 'update' && campaign.value) {
        await rollbackToDraft(campaign.value.id)
        return
      }
      throw new Error('publish-failed')
    }

    if (mode === 'update') {
      // Convergent dual-write: after the ME1 edit commits (updateCampaign +
      // publishChanges), push the whole desired state to ME2 so an
      // ME2-dispatched send picks up the edit. Flag-gated and best-effort — fired
      // and NOT awaited (the helper swallows errors and self-bounds with a
      // timeout) so an ME2 hiccup never delays the dialog close. Olympus
      // re-resolves subject/sections/audience server-side, so this is just the
      // trigger; no schedule is sent (reschedule owns `/schedule`).
      if (me2ConvergentEditEnabled.value && editedCampaignId) {
        void sendMe2ConvergentEdit(
          editedCampaignId,
          {
            locationId: locationId.value || undefined,
            subject: subject.value,
          },
          // `update` mode is reachable only from these two actions (see `actionType`).
          props.action === 'audience' ? 'audience' : 'subject'
        )
      }
      showDialog.value = false
    } else {
      if (!campaign.value) throw new Error('schedule-failed')
      const campaignId = campaign.value.id
      try {
        await scheduleCampaign({
          body: {
            executeAt: scheduledFor.toISOString(),
            subject: subject.value,
            // Forward the campaign's location on every action, not just
            // `audience`. `locationId` is seeded from the stored campaign in
            // `initialize()`, so this re-asserts the existing scope; the
            // endpoint strips an empty/undefined value, so a whole-brand
            // campaign stays whole-brand. Gating on `audience` made the
            // field-presence asymmetric with `subject` for no behavioral gain.
            ...(locationId.value ? { locationId: locationId.value } : {}),
          },
          params: { campaignId },
        })
      } catch (error) {
        // Publish already advanced the draft; a schedule failure here would
        // otherwise strand the campaign published-but-unscheduled. Roll it back
        // to draft, mirroring the publish-failure path above.
        logError(error)
        await rollbackToDraft(campaignId)
        return
      }
      // Re-fetch the transformed resource (the `/schedule` response is not it)
      // and feed the BARE `resource` to the builder's reset sink, so
      // `currentResource` stays the composite transformed resource. This keeps
      // CampaignControls, the sidebar, and the confirmation modal (which reads
      // `executeAt` to show "Scheduled" vs "Sent") in sync after the schedule.
      // Best-effort: the schedule is already committed, so a refresh failure
      // must never roll back, surface a schedule error, or block `submit`.
      try {
        if (brandId) {
          const refreshed = await props.methods.fetchDataHandler({
            brandId,
            resourceId: campaignId,
          })
          await resetResource(refreshed.resource)
        }
      } catch (refreshError) {
        logError(refreshError)
      }

      emit('submit')
    }
  } catch (error) {
    logError(error)
    notify(t(`errors.${mode}`), 'error')
  } finally {
    isSaving.value = false
  }
}

watch(campaign, initialize)
// Re-initialize when `action` changes
watch(() => props.action, initialize)

onMounted(initialize)
</script>
